// client/src/components/pages/VehicleRouteTracker/index.tsx (converted from .js, ST-00218 P4:
// typed; the trips answer's `matching` block reaches the trip timeline, which shows the
// fallback note when the trips were paired by delivery number instead of truck + time).
// ST-00242: the truck is picked from the period's registrations (VehicleSearch).
import { useState, useCallback } from 'react';
import { FaTruck } from 'react-icons/fa';
import api from '../../../services/api';
import UnifiedDateRangePicker from '../../common/UnifiedDateRangePicker';
import VehicleSearch from './VehicleSearch';
import StatsCards from './StatsCards';
import TripTimeline from './TripTimeline';
import RouteNetworkDiagram from './RouteNetworkDiagram';
import DailyActivityChart from './DailyActivityChart';
import { PageHeader } from '../../../types/commonComponents';
import type { MatchingInfo } from '../../../types/routeFigures';
import type {
  VehicleTrip,
  VehicleTripsPagination,
  VehicleTripsResponse,
} from '../../../types/clientPages/vehicleRouteTracker';

/** GET /vehicle-tracker/:regNo/stats (rendered by StatsCards). */
type VehicleStats = Record<string, unknown>;
/** GET …/route-network and …/daily-activity rows (rendered by their charts). */
type ChartRow = Record<string, unknown>;

const toDateString = (date: Date | string | null | undefined): string | null => {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const VehicleRouteTracker = () => {
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    if (today.getDate() === 1) {
      return new Date(today.getFullYear(), today.getMonth() - 1, 1);
    }
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    if (today.getDate() === 1) {
      return new Date(today.getFullYear(), today.getMonth(), 0);
    }
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d;
  });

  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);

  // Data states
  const [stats, setStats] = useState<VehicleStats | null>(null);
  const [trips, setTrips] = useState<VehicleTrip[]>([]);
  const [tripsPagination, setTripsPagination] = useState<VehicleTripsPagination | null>(null);
  // ST-00218 P4: how the trips were paired (first page's answer)
  const [tripsMatching, setTripsMatching] = useState<MatchingInfo | null>(null);
  const [routeNetwork, setRouteNetwork] = useState<ChartRow[]>([]);
  const [dailyActivity, setDailyActivity] = useState<ChartRow[]>([]);

  // Independent loading states for progressive rendering
  const [statsLoading, setStatsLoading] = useState(false);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadVehicleData = useCallback(async (regNo: string, start: Date, end: Date) => {
    setError(null);
    setStats(null);
    setTrips([]);
    setTripsPagination(null);
    setTripsMatching(null);
    setRouteNetwork([]);
    setDailyActivity([]);

    // The dates are always set (the picker never clears them).
    const startStr = toDateString(start) as string;
    const endStr = toDateString(end) as string;

    // Load stats first (fastest, shown at top)
    setStatsLoading(true);
    api.vehicleTracker
      .getVehicleStats(regNo, startStr, endStr)
      .then((res) => setStats(res.data as VehicleStats))
      .catch((err) => {
        console.error('Error loading stats:', err);
        setError('Failed to load vehicle stats.');
      })
      .finally(() => setStatsLoading(false));

    // Load trips independently
    setTripsLoading(true);
    api.vehicleTracker
      .getVehicleTrips(regNo, {
        startDate: startStr,
        endDate: endStr,
        page: 1,
        limit: 50,
      })
      .then((res) => {
        const data = res.data as VehicleTripsResponse;
        setTrips(data.trips);
        setTripsPagination(data.pagination);
        setTripsMatching(data.matching ?? null);
      })
      .catch((err) => console.error('Error loading trips:', err))
      .finally(() => setTripsLoading(false));

    // Load charts independently
    setChartsLoading(true);
    Promise.all([
      api.vehicleTracker.getRouteNetwork(regNo, startStr, endStr),
      api.vehicleTracker.getDailyActivity(regNo, startStr, endStr),
    ])
      .then(([networkRes, activityRes]) => {
        setRouteNetwork(networkRes.data as ChartRow[]);
        setDailyActivity(activityRes.data as ChartRow[]);
      })
      .catch((err) => console.error('Error loading charts:', err))
      .finally(() => setChartsLoading(false));
  }, []);

  const handleVehicleSelect = useCallback(
    (regNo: string) => {
      setSelectedVehicle(regNo);
      loadVehicleData(regNo, startDate, endDate);
    },
    [loadVehicleData, startDate, endDate]
  );

  const handleStartDateChange = useCallback(
    (date: Date) => {
      setStartDate(date);
      if (selectedVehicle) {
        loadVehicleData(selectedVehicle, date, endDate);
      }
    },
    [selectedVehicle, endDate, loadVehicleData]
  );

  const handleEndDateChange = useCallback(
    (date: Date) => {
      setEndDate(date);
      if (selectedVehicle) {
        loadVehicleData(selectedVehicle, startDate, date);
      }
    },
    [selectedVehicle, startDate, loadVehicleData]
  );

  const handleLoadMore = useCallback(async () => {
    if (!selectedVehicle || !tripsPagination || tripsLoading) return;

    const nextPage = tripsPagination.page + 1;
    if (nextPage > tripsPagination.totalPages) return;

    setTripsLoading(true);
    try {
      const res = await api.vehicleTracker.getVehicleTrips(selectedVehicle, {
        startDate: toDateString(startDate),
        endDate: toDateString(endDate),
        page: nextPage,
        limit: 50,
      });
      const data = res.data as VehicleTripsResponse;
      setTrips((prev) => [...prev, ...data.trips]);
      setTripsPagination(data.pagination);
    } catch (err) {
      console.error('Error loading more trips:', err);
    } finally {
      setTripsLoading(false);
    }
  }, [selectedVehicle, tripsPagination, tripsLoading, startDate, endDate]);

  const isAnyLoading = statsLoading || tripsLoading || chartsLoading;

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={FaTruck}
        title="Vehicle Route Tracker"
        subtitle="Trace vehicle journeys across dispatch and receipt locations"
        toolbar={
          <div className="sm:w-64">
            <UnifiedDateRangePicker
              startDate={startDate}
              onStartDateChange={handleStartDateChange}
              endDate={endDate}
              onEndDateChange={handleEndDateChange}
            />
          </div>
        }
      />

      <div className="space-y-6 p-6">
        {/* Vehicle Search */}
        <VehicleSearch
          onSelect={handleVehicleSelect}
          selectedVehicle={selectedVehicle}
          startDate={toDateString(startDate)}
          endDate={toDateString(endDate)}
        />

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Empty state */}
        {!selectedVehicle && !isAnyLoading && (
          <div className="bg-surface rounded-xl border border-slate-200 shadow-sm px-8 py-16 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FaTruck className="w-8 h-8 text-slate-300" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 mb-1">Pick a vehicle</h3>
            <p className="text-sm text-slate-400 max-w-sm mx-auto">
              Choose a truck from the list above, or type its registration, to view its route
              history, trip timeline, and movement patterns.
            </p>
          </div>
        )}

        {/* Progressive data display - each section renders independently */}
        {selectedVehicle && (
          <>
            {/* Stats Cards - loads first */}
            {statsLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-surface rounded-xl border border-slate-200 p-4 animate-pulse"
                  >
                    <div className="h-3 w-20 bg-slate-200 rounded mb-3" />
                    <div className="h-6 w-16 bg-slate-200 rounded" />
                  </div>
                ))}
              </div>
            ) : stats ? (
              <StatsCards stats={stats} />
            ) : null}

            {/* Main content: Timeline + Side panels */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Timeline - 2/3 */}
              <div className="lg:col-span-2">
                {tripsLoading && trips.length === 0 ? (
                  <div className="bg-surface rounded-xl border border-slate-200 p-8 text-center">
                    <div className="w-6 h-6 border-2 border-primary-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-sm text-slate-500">Loading trip timeline...</p>
                  </div>
                ) : (
                  <TripTimeline
                    trips={trips}
                    pagination={tripsPagination}
                    onLoadMore={handleLoadMore}
                    loading={tripsLoading}
                    matching={tripsMatching}
                  />
                )}
              </div>

              {/* Side panels - 1/3 */}
              <div className="space-y-6">
                {chartsLoading ? (
                  <>
                    <div className="bg-surface rounded-xl border border-slate-200 p-8 text-center animate-pulse">
                      <div className="h-3 w-32 bg-slate-200 rounded mx-auto mb-4" />
                      <div className="h-32 bg-slate-100 rounded" />
                    </div>
                    <div className="bg-surface rounded-xl border border-slate-200 p-8 text-center animate-pulse">
                      <div className="h-3 w-32 bg-slate-200 rounded mx-auto mb-4" />
                      <div className="h-32 bg-slate-100 rounded" />
                    </div>
                  </>
                ) : (
                  <>
                    <RouteNetworkDiagram data={routeNetwork} />
                    <DailyActivityChart data={dailyActivity} />
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VehicleRouteTracker;
